import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Platform, RefreshControl, ScrollView, StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import StatusContainer from '@/components/StatusContainer';
import ToolGrid from '@/components/Tools/ToolGrid';
import ToolListItem, { ToolListItemPlaceholder } from '@/components/Tools/ToolListItem';
import LoadMoreButton from '@/components/Buttons/LoadMoreButton';
import CatalogSortDropdown from '@/components/Catalog/CatalogSortDropdown';
import ToolsHero from '@/components/Tools/ToolsHero';
import ToolsDirectory from '@/components/Tools/ToolsDirectory';
import SavedTools from '@/components/Tools/SavedTools';
import CatalogBrowseShell, { CATALOG_VIEW_MODES } from '@/components/Catalog/CatalogBrowseShell';
import AppDashboardFooterBottomBar from '@/components/AppDashboardFooterBottomBar';
import { useToolStore, useAuthStore } from '@/store';
import { toolApi as toolApiSsr } from '@/lib/toolApi';

// Module-level cache for build-time SEO data. Populated by `getStaticTools()`
// the first time the page renders during static export (and reused by the
// JSON-LD ItemList so crawlers see the catalog enumerated in the static
// HTML, not just after JS hydration). Empty array on the client.
let _staticToolsCache = null;
async function getStaticTools() {
  if (_staticToolsCache) return _staticToolsCache;
  try {
    const data = await toolApiSsr.listTools({ limit: 200 });
    _staticToolsCache = data.tools || [];
  } catch {
    _staticToolsCache = [];
  }
  return _staticToolsCache;
}

// Pre-resolve catalog at module load so the JSON-LD ItemList below is
// populated when expo-router serializes this page during static export.
if (typeof window === 'undefined') {
  getStaticTools().catch(() => {});
}

const VIEW_MODES = CATALOG_VIEW_MODES;

const TOOLS_CATALOG_SCROLL_KEY = 'gab_tools_catalog_scroll_y';

// ─── Styles ────────────────────────────────────────────────────────────────

// `isXS` ⊂ `isSM` ⊂ `isMD` ⊂ `isLG` (each ≤-bound), so style decisions read
// most-narrow-first to keep the cascade unambiguous.
const createStyles = (colors, isXS, isSM, isMD, isXL, insetBottom = 0, hideHeaderBorder = false) => {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.solid_color_primary,
      borderTopWidth: hideHeaderBorder ? 0 : 1,
      borderTopColor: hideHeaderBorder ? 'transparent' : colors.border_color_secondary,
    },
    scrollContainer: {
      flex: 1,
      width: '100%',
    },
    scrollContent: {
      width: '100%',
      paddingBottom: insetBottom + 40,
    },
    contentWrap: {
      paddingHorizontal: 0,
      maxWidth: 1240,
      width: '100%',
      alignSelf: 'center',
    },
    section: {
      marginTop: isXS ? 14 : 20,
    },
    blockHeader: {
      flexDirection: 'row',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: 12,
      marginBottom: 14,
    },
    blockTitle: {
      fontSize: isXS ? 18 : 20,
      letterSpacing: -0.4,
    },
    blockCount: {
      fontVariant: ['tabular-nums'],
    },
    block: {
      width: '100%',
    },
    toolListStack: {
      width: '100%',
      gap: 8,
    },
    recommendSection: {
      marginTop: 4,
    },
    loadMore: {
      marginTop: 16,
      alignSelf: 'center',
      minWidth: 220,
    },
    footerWrap: {
      paddingHorizontal: isXS ? 16 : 28,
    },
  });
};

// ─── Page ──────────────────────────────────────────────────────────────────

// `categorySlug` mirrors the tasks page contract: the path-based route
// `/tools/category/[slug]` renders this same page with the prop set,
// which always wins over a stale `?category=` query string.
export const ToolsPage = React.memo(({ categorySlug: categorySlugProp = null } = {}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS, isSM, isMD, isXL } = useResponsiveness();
  const router = useRouter();
  const params = useLocalSearchParams();
  const insets = useSafeAreaInsets();
  const [refreshing, setRefreshing] = useState(false);
  const [directoryRefresh, setDirectoryRefresh] = useState(0);
  const [isNearTop, setIsNearTop] = useState(true);
  const scrollViewRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState(VIEW_MODES.GRID);
  const [catalogSort, setCatalogSort] = useState('relevant');

  const userId = useAuthStore(s => s.user?.id);
  const hasUser = Boolean(userId);

  const styles = useMemo(
    () => createStyles(colors, isXS, isSM, isMD, isXL, insets.bottom, isNearTop),
    [colors, isXS, isSM, isMD, isXL, insets.bottom, isNearTop]
  );

  const toolsBySlug      = useToolStore(s => s.toolsBySlug);
  const catalogSlugs     = useToolStore(s => s.catalogSlugs);
  const featuredSlugs    = useToolStore(s => s.featuredSlugs);
  const isLoading        = useToolStore(s => s.isLoadingCatalog);
  const isLoadingMore    = useToolStore(s => s.isLoadingMoreCatalog);
  const error            = useToolStore(s => s.catalogError);
  const catalogTotal     = useToolStore(s => s.catalogTotal);
  const catalogHasMore   = useToolStore(s => s.catalogHasMore);
  const catalogActiveFilters = useToolStore(s => s.catalogActiveFilters);
  const fetchCatalog     = useToolStore(s => s.fetchCatalog);
  const loadMoreCatalog  = useToolStore(s => s.loadMoreCatalog);
  const fetchFeatured    = useToolStore(s => s.fetchFeatured);
  const categories          = useToolStore(s => s.categories);
  const isFetchedCategories = useToolStore(s => s.isFetchedCategories);
  const isLoadingCategories = useToolStore(s => s.isLoadingCategories);
  const fetchCategories     = useToolStore(s => s.fetchCategories);
  const categoriesError     = useToolStore(s => s.categoriesError);

  // Resolve the active category from the path prop first, then the
  // legacy `?category=` query. Either form keeps working unchanged.
  const urlCategory = useMemo(() => {
    const raw = params?.category;
    if (Array.isArray(raw)) return raw[0] || null;
    return typeof raw === 'string' && raw ? raw : null;
  }, [params?.category]);
  const activeCategory = categorySlugProp || urlCategory || null;

  const catalogMatchesFilters = (catalogActiveFilters.q || '') === searchQuery.trim()
    && (catalogActiveFilters.category || null) === activeCategory;

  const allTools = useMemo(
    () => catalogSlugs.map(slug => toolsBySlug[slug]).filter(Boolean),
    [catalogSlugs, toolsBySlug]
  );

  const featuredTools = useMemo(
    () => featuredSlugs.map(slug => toolsBySlug[slug]).filter(Boolean),
    [featuredSlugs, toolsBySlug]
  );

  // O(1) lookup keyed by slug → position in the canonical catalog order.
  // Computed once per catalog change so the sort comparators below stay
  // linearithmic instead of quadratic-ish (`Array.indexOf` was previously
  // called inside the comparator, which is O(n) per call).
  const catalogIndexBySlug = useMemo(() => {
    const map = new Map();
    for (let i = 0; i < catalogSlugs.length; i++) map.set(catalogSlugs[i], i);
    return map;
  }, [catalogSlugs]);

  const sortedFilteredTools = useMemo(() => {
    const list = [...allTools];
    const catalogIndex = (slug) => {
      const i = catalogIndexBySlug.get(slug);
      return i == null ? Number.MAX_SAFE_INTEGER : i;
    };
    const usageCount = (tool) => Number(tool?.stats?.usageCount) || 0;
    switch (catalogSort) {
      case 'relevant':
        return list.sort((a, b) => catalogIndex(a.slug) - catalogIndex(b.slug));
      case 'popular':
        return list.sort((a, b) => {
          const ua = usageCount(a);
          const ub = usageCount(b);
          if (ub !== ua) return ub - ua;
          return catalogIndex(a.slug) - catalogIndex(b.slug);
        });
      case 'name-asc':
        return list.sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
        );
      case 'name-desc':
        return list.sort((a, b) =>
          (b.name || '').localeCompare(a.name || '', undefined, { sensitivity: 'base' })
        );
      case 'category': {
        return list.sort((a, b) => {
          const aHas = typeof a.category === 'object' && a.category?.name;
          const bHas = typeof b.category === 'object' && b.category?.name;
          if (aHas && !bHas) return -1;
          if (!aHas && bHas) return 1;
          if (!aHas && !bHas) {
            return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
          }
          const byCat = a.category.name.localeCompare(b.category.name, undefined, { sensitivity: 'base' });
          if (byCat !== 0) return byCat;
          return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
        });
      }
      default:
        return list;
    }
  }, [allTools, catalogSort, catalogIndexBySlug]);

  // Featured rail + taxonomy load once on mount — neither depends on the
  // active filters, so they don't need to refetch when search / category
  // change.
  useEffect(() => {
    fetchFeatured(8).catch(() => {});
    if (!isFetchedCategories && !isLoadingCategories) {
      fetchCategories().catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Server-side search + category filtering. Re-fetches the catalog every
  // time the user types (with a small debounce so we don't fire on every
  // keystroke) or switches category. The store resets pagination on each
  // call, so "Load more" picks up under the new filters.
  //
  // On the initial mount this also doubles as the first catalog fetch —
  // with empty `searchQuery` and `null` activeCategory it fires
  // `fetchCatalog({})` after a 0ms tick.
  useEffect(() => {
    const trimmed = searchQuery.trim();
    const filters = {};
    if (trimmed) filters.q = trimmed;
    if (activeCategory) filters.category = activeCategory;
    const handle = setTimeout(() => {
      fetchCatalog(filters).catch(() => {});
    }, trimmed ? 280 : 0);
    return () => clearTimeout(handle);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchCatalog is stable
  }, [searchQuery, activeCategory]);

  // Start each category at its heading, with an easy way back to all tools.
  const catalogScrollRestoreKey = `${categorySlugProp || ''}|${urlCategory || ''}`;
  useLayoutEffect(() => {
    if (Platform.OS !== 'web' || typeof sessionStorage === 'undefined') return;
    let raw;
    try {
      raw = sessionStorage.getItem(TOOLS_CATALOG_SCROLL_KEY);
    } catch {
      return;
    }
    if (raw == null) return;
    try {
      sessionStorage.removeItem(TOOLS_CATALOG_SCROLL_KEY);
    } catch {
      /* ignore */
    }
    const y = parseInt(raw, 10);
    if (!Number.isFinite(y) || y < 0) return;
    requestAnimationFrame(() => {
      scrollViewRef.current?.scrollTo?.({ y, animated: false });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed restore only
  }, [catalogScrollRestoreKey]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    setDirectoryRefresh(value => value + 1);
    try {
      const trimmed = searchQuery.trim();
      const filters = {};
      if (trimmed) filters.q = trimmed;
      if (activeCategory) filters.category = activeCategory;
      await Promise.all([fetchCatalog(filters), fetchFeatured(8), fetchCategories({ force: true })]);
    } finally {
      setRefreshing(false);
    }
  }, [fetchCatalog, fetchFeatured, fetchCategories, searchQuery, activeCategory]);

  // "Load more" handler — paginates forward under the filters last sent
  // to `fetchCatalog` (the store carries them in `catalogActiveFilters`),
  // so search + category context survive the click.
  const handleLoadMore = useCallback(() => {
    loadMoreCatalog().catch(() => {});
  }, [loadMoreCatalog]);

  const handleToolPress = useCallback((tool) => {
    if (tool?.slug) router.push(`/tools/${tool.slug}`);
  }, [router]);

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
  }, []);

  const isSearching = !!searchQuery.trim();

  const recommendedToolsForEmptySearch = useMemo(() => {
    if (!searchQuery.trim()) return [];

    const limit = isXS ? 4 : 8;
    const seen = new Set();
    const out = [];

    const add = (tool) => {
      const key = tool?.slug || tool?.id || tool?._id;
      if (!tool || !key || seen.has(String(key))) return;
      seen.add(String(key));
      out.push(tool);
    };

    for (const tool of featuredTools) {
      if (out.length >= limit) break;
      add(tool);
    }

    if (out.length < limit) {
      const byUsage = [...allTools].sort(
        (a, b) => (Number(b?.stats?.usageCount) || 0) - (Number(a?.stats?.usageCount) || 0)
      );
      for (const tool of byUsage) {
        if (out.length >= limit) break;
        add(tool);
      }
    }

    return out;
  }, [searchQuery, featuredTools, allTools, isXS]);

  // Push the URL alongside the local update so each scope is shareable
  // and SEO-friendly via the path-based category routes.
  const handleCategoryChange = useCallback((slug) => {
    if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.setItem(
          TOOLS_CATALOG_SCROLL_KEY,
          '0',
        );
      } catch {
        /* ignore quota / private mode */
      }
    }
    if (!slug) {
      router.push('/tools');
    } else {
      router.push(`/tools/category/${encodeURIComponent(slug)}`);
    }
  }, [router]);

  const railCategories = useMemo(
    () => (categories || []).filter(c => c?.slug),
    [categories]
  );

  // Catalog JSON-LD — CollectionPage + ItemList of every active tool. The
  // ItemList is built from the build-time cache when available (static
  // export) and the live Zustand store at runtime, so crawlers see the
  // full catalog and humans always see the freshest copy.
  const seoStructuredData = useMemo(() => {
    const cacheList = Array.isArray(_staticToolsCache) ? _staticToolsCache : [];
    const liveList = allTools || [];
    const sourceList = liveList.length ? liveList : cacheList;
    const itemList = sourceList.slice(0, 100).map((tool, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      url: `https://gab.ai/tools/${tool.slug}`,
      name: tool?.meta?.title || tool?.name,
    }));
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          name: 'AI Tools',
          description: 'Specialized single-purpose AI tools for content creation, image generation, video, music, code, analysis, and more.',
          url: 'https://gab.ai/tools',
          isPartOf: { '@type': 'WebSite', name: 'Gab AI', url: 'https://gab.ai' },
          inLanguage: 'en-US',
          mainEntity: { '@type': 'ItemList', itemListElement: itemList },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://gab.ai' },
            { '@type': 'ListItem', position: 2, name: 'AI Tools', item: 'https://gab.ai/tools' },
          ],
        },
      ],
    };
  }, [allTools]);

  const toolsCatalogSortOptions = useMemo(() => [
    { id: 'relevant', label: t('pages.tools.catalogSortRelevant') || 'Relevant', icon: 'bars-staggered', iosIcon: 'line.3.horizontal.decrease' },
    { id: 'popular', label: t('pages.tools.catalogSortPopular') || 'Popular', icon: 'fire', iosIcon: 'flame' },
    { id: 'name-asc', label: t('pages.tools.catalogSortNameAsc') || 'Name A–Z', icon: 'arrow-down-a-z', iosIcon: 'textformat.abc' },
    { id: 'name-desc', label: t('pages.tools.catalogSortNameDesc') || 'Name Z–A', icon: 'arrow-up-z-a', iosIcon: 'textformat.abc' },
    { id: 'category', label: t('pages.tools.catalogSortCategory') || 'By category', icon: 'layer-group', iosIcon: 'square.stack.3d.up' },
  ], [t]);

  const toolsDiscoveryHeroSearch = useMemo(() => ({
    value: searchQuery,
    onChangeText: setSearchQuery,
    placeholder: t('pages.tools.searchPlaceholder') || 'Search tools…',
    testID: 'tools-catalog-search',
  }), [searchQuery, t]);

  const toolsBrowseToolbarTrailing = useMemo(() => (
    <CatalogSortDropdown value={catalogSort} options={toolsCatalogSortOptions}
      onChange={setCatalogSort} label={t('pages.tools.catalogSort') || 'Sort'} />
  ), [t, catalogSort, toolsCatalogSortOptions]);

  const toolsBrowseToolbarBase = useMemo(() => ({
    viewMode,
    listMode: VIEW_MODES.LIST,
    gridMode: VIEW_MODES.GRID,
    onViewModeChange: handleViewModeChange,
    trailing: toolsBrowseToolbarTrailing,
  }), [viewMode, handleViewModeChange, toolsBrowseToolbarTrailing]);

  const activeCategoryRecord = useMemo(
    () => (activeCategory ? railCategories.find(c => c.slug === activeCategory) : null),
    [activeCategory, railCategories]
  );

  // Browse sections follow the taxonomy order.
  const heroCategories = useMemo(
    () => [...railCategories].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0)),
    [railCategories]
  );

  const heroCopy = useMemo(() => {
    if (activeCategory) {
      const name = activeCategoryRecord?.name
        || activeCategory.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      return {
        headline: name,
        subtitle: activeCategoryRecord?.description || null,
      };
    }
    return {
      headline: t('navigation.tools'),
      subtitle: t('pages.tools.heroLead')
        || 'Purpose-built tools for writing, images, video, music, code, and analysis. Fill in a few fields, get a finished result, then keep going in chat.',
    };
  }, [activeCategory, activeCategoryRecord, t]);

  const handleHeroCategoryChange = handleCategoryChange;
  const showDirectory = !isSearching && !activeCategory;

  // ── Sub-renderers ────────────────────────────────────────────────────────

  const renderToolCollection = (tools) => (
    viewMode === VIEW_MODES.LIST ? (
      <View style={styles.toolListStack}>
        {tools.map(tool => (
          <ToolListItem
            key={tool.id || tool._id || tool.slug}
            tool={tool}
            onPress={handleToolPress}
          />
        ))}
      </View>
    ) : (
      <ToolGrid tools={tools} onToolPress={handleToolPress} />
    )
  );

  const renderCatalog = () => {
    const headerLabel = isSearching
      ? `${t('pages.tasks.resultsFor') || 'Results for'} "${searchQuery.trim()}"`
      : activeCategoryRecord
        ? (t('pages.tools.categoryTools') || '%NAME% tools').replace('%NAME%', activeCategoryRecord.name || activeCategoryRecord.slug)
        : (t('pages.tools.allTools') || 'All tools');

    const showSearchRecommendations = isSearching
      && !error
      && catalogMatchesFilters
      && !isLoading
      && !sortedFilteredTools.length
      && recommendedToolsForEmptySearch.length > 0;

    const headerCount = catalogTotal || sortedFilteredTools.length;

    const showLoadMore =
      catalogHasMore
      && !error
      && catalogMatchesFilters
      && !isLoading
      && sortedFilteredTools.length > 0;

    const headerCountLabel = headerCount === 1
      ? (t('pages.tools.toolsCountSingle') || '1 tool')
      : (t('pages.tools.toolsCount') || '%COUNT% tools').replace('%COUNT%', String(headerCount));

    return (
      <View style={styles.block}>
        <View style={styles.blockHeader}>
          {(!activeCategory || isSearching) && <Text tag="h2" weight="bold" style={[styles.blockTitle, { flex: 1 }]}>{headerLabel}</Text>}
          {!isLoading && catalogMatchesFilters && headerCount > 0 ? (
            <Text size="s" color="tertiary" style={styles.blockCount}>{headerCountLabel}</Text>
          ) : null}
        </View>

        {error && !isLoading && !sortedFilteredTools.length ? (
          <StatusContainer
            type='error'
            message={error}
            actionText={t('pages.common.retry') || 'Retry'}
            onAction={onRefresh}
          />
        ) : isLoading || !catalogMatchesFilters ? (
          viewMode === VIEW_MODES.LIST ? (
            <View style={styles.toolListStack}>
              {[...Array(isXS ? 5 : 8)].map((_, i) => (
                <ToolListItemPlaceholder key={i} />
              ))}
            </View>
          ) : (
            <ToolGrid isLoading placeholderCount={isXS ? 4 : 8} />
          )
        ) : showSearchRecommendations ? (
          <View style={styles.recommendSection}>
            <View style={styles.blockHeader}>
              <Text weight="bold" size="m" style={{ flex: 1, minWidth: 0 }}>
                {t('pages.tools.noSearchResultsRecommend') || 'No results for your search but check these out'}
              </Text>
            </View>
            {renderToolCollection(recommendedToolsForEmptySearch)}
          </View>
        ) : !sortedFilteredTools.length ? (
          <StatusContainer
            type='empty'
            message={
              isSearching || activeCategory
                ? (t('pages.tools.noResultsForQuery') || 'No tools match your search.')
                : (t('pages.tools.noToolsYet') || 'No tools available yet.')
            }
          />
        ) : (
          <>
            {renderToolCollection(sortedFilteredTools)}
            {showLoadMore ? (
              <LoadMoreButton
                onPress={handleLoadMore}
                isLoading={isLoadingMore}
                isLarge
                style={styles.loadMore}
              />
            ) : null}
          </>
        )}
      </View>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      {!categorySlugProp && (
        <MetaHead
          title={t('pages.tools.title') || 'AI Tools — Single-Purpose AI for Every Task'}
          description={t('pages.tools.description') || 'Browse Gab AI\u2019s library of specialized AI tools for writing, image and video generation, music, code, analysis, and more. Fill out a form, get a result, then continue in chat.'}
          canonical="/tools"
          keywords="AI tools, AI utilities, AI generators, AI writing, AI image, AI video, AI music, AI assistants"
          structuredData={seoStructuredData}
        />
      )}
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        onScroll={(e) => {
          setIsNearTop(e.nativeEvent.contentOffset.y < 10);
        }}
        scrollEventThrottle={16}
        // Native: tapping a card while the search keyboard is up should fire
        // the card press (default behavior would just dismiss the keyboard
        // and swallow the tap). Dragging the list also dismisses the
        // keyboard naturally — both are baseline iOS expectations.
        keyboardShouldPersistTaps='handled'
        keyboardDismissMode={Platform.OS === 'ios' || Platform.OS === 'android' ? 'on-drag' : 'none'}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={colors.text_color_secondary}
            colors={[colors.text_color_secondary]}
          />
        }
      >
        <View style={styles.contentWrap}>
          <ToolsHero
            headline={heroCopy.headline}
            subtitle={heroCopy.subtitle}
            search={toolsDiscoveryHeroSearch}
            onHistory={Platform.OS === 'web' && !isXS && hasUser ? () => router.push('/tools/history') : undefined}
            onBack={activeCategory ? () => handleHeroCategoryChange(null) : null}
          />

          <View style={styles.section}>
            <CatalogBrowseShell
              isXS={isXS}
              bodyGap={18}
              hideRail
              suppressMobilePills
              toolbar={showDirectory ? null : toolsBrowseToolbarBase}
            >
              {showDirectory && hasUser && <SavedTools key={userId} onToolPress={handleToolPress} refreshKey={directoryRefresh} />}
              {showDirectory ? (
                categoriesError ? <StatusContainer type="error" message={categoriesError}
                  actionText={t('pages.common.retry')} onAction={() => fetchCategories({ force: true })} />
                  : !isFetchedCategories ? <ToolGrid isLoading placeholderCount={3} />
                    : heroCategories.length ? <ToolsDirectory categories={heroCategories}
                      onCategoryPress={handleHeroCategoryChange} onToolPress={handleToolPress} refreshKey={directoryRefresh} />
                      : renderCatalog()
              ) : renderCatalog()}

            </CatalogBrowseShell>
          </View>

          <View style={styles.footerWrap}>
            <AppDashboardFooterBottomBar metaText="Gab AI" termsLabel={t('common.termsOfService')}
              privacyLabel={t('common.privacyPolicy')} onNavigate={path => router.push(path)} />
          </View>
        </View>
      </ScrollView>
    </View>
  );
});

ToolsPage.displayName = 'ToolsPage';

export default function ToolsRoute() {
  return <ToolsPage />;
}
